import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Container, Divider, Paper, Skeleton, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";

import InfoSection, { InformationField } from "../../components/general/InfoSection";
import WorkshopInstanceFeedbackForm from "../../components/workshops/workshopFeedback/WorkshopInstanceFeedbackForm";

import useResponsive from "../../hooks/useResponsive";
import { AuthContext } from "../../context/auth-context/AuthContext";
import useWorkshopInstanceDetails from "../../hooks/workshops/useWorkshopInstanceDetails";
import useWorkshopDetails from "../../hooks/workshops/useWorkshopDetails";
import { WorkshopInstanceUserFeedbackDto } from "../../types/workshopTypes";

const workshopInstanceTextQuestions: { [key: string]: string } = {
  workshopLevel: "Wie würdest Du den Workshop insgesamt bewerten?",
  contentFeedback: "Welche inhaltlichen Aspekte sollten ausgebaut, ergänzt oder gestrichen werden?",
  presentationFeedback: "Worin siehst Du Verbesserungsmöglichkeiten bezüglich der Präsentation?",
  workshopNeed: "Welche zusätzlichen Schulungen sind für Dich von Interesse?",
};

/**
 * Displays the feedback form for a workshop instance
 * @returns The WorkshopInstanceFeedback component
 */
const WorkshopInstanceFeedback = () => {
  const { workshopID, workshopInstanceID } = useParams<{
    workshopID: string;
    workshopInstanceID: string;
  }>();
  const { auth } = useContext(AuthContext);

  const { workshopInstanceDetails, workshopInstanceFeedbackQuestions, submitWorkshopFeedback } =
    useWorkshopInstanceDetails(Number(workshopID), Number(workshopInstanceID));
  const { workshopDetails } = useWorkshopDetails(Number(workshopID));

  const isMobile = useResponsive("down", "sm");

  const [userFeedback, setUserFeedback] = useState<WorkshopInstanceUserFeedbackDto>({
    workshopInstanceId: Number(workshopInstanceID),
    workshopId: Number(workshopID),
    feedback: [],
    referentsFeedback: [],
    workshopLevel: "",
    contentFeedback: "",
    presentationFeedback: "",
    workshopNeed: "",
  });

  // Initialize feedback responses when questions are loaded
  useEffect(() => {
    if (workshopInstanceFeedbackQuestions && userFeedback) {
      const initialFeedback = workshopInstanceFeedbackQuestions.map((question) => ({
        feedbackQuestionId: question.feedbackQuestionId,
        grade: undefined,
      }));

      setUserFeedback((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          feedback: initialFeedback,
        };
      });
    }
  }, [workshopInstanceFeedbackQuestions]);

  // Initialize feedback responses when workshopDetails are loaded
  useEffect(() => {
    if (workshopInstanceDetails && userFeedback) {
      const initialFeedback = workshopInstanceDetails.referents.map((referent) => ({
        memberId: referent.memberId,
        grade: undefined,
      }));

      setUserFeedback((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          referentsFeedback: initialFeedback,
        };
      });
    }
  }, [workshopInstanceDetails]);

  // Helper function to update feedback response for a specific question
  const updateFeedbackGrade = (questionId: number, value: number) => {
    setUserFeedback((prev) => {
      if (!prev) return prev;

      const updatedFeedback = prev.feedback.map((item) =>
        item.feedbackQuestionId === questionId ? { ...item, grade: value } : item
      );

      return {
        ...prev,
        feedback: updatedFeedback,
      };
    });
  };

  // Helper function to update feedback response for a specific referent
  const updateReferentFeedbackGrade = (memberId: number, value: number) => {
    setUserFeedback((prev) => {
      if (!prev) return prev;
      const updatedFeedback = prev.referentsFeedback.map((item) =>
        item.memberId === memberId ? { ...item, grade: value } : item
      );

      return {
        ...prev,
        referentsFeedback: updatedFeedback,
      };
    });
  };

  // Checks if the user is participating in the workshop instance
  const checkParticipation = () => {
    if (workshopInstanceDetails) {
      // Check if the user is in the participants list of the workshop instance details and if the type of the participant object is "Teilnehmer"
      return (
        workshopInstanceDetails.participants.find(
          (participant) =>
            participant.memberId === Number(auth.userID) && participant.type === "Teilnehmer" && participant.present
        ) !== undefined
      );
      // if the workshopInstanceDetails return false
    } else {
      return false;
    }
  };

  // Checks if the user has already given feedback for the workshop instance
  const checkFeedbackGiven = () => {
    if (checkParticipation()) {
      // return the feedbackGiven value of the participant object of the user in the participants list of the workshop instance details
      return workshopInstanceDetails?.participants.find((participant) => participant.memberId === Number(auth.userID))
        ?.feedbackGiven;
      // if the user is not participating, we can assume that they have not given feedback, so we return true to not show the feedback form
    } else {
      return true;
    }
  };

  const WorkshopInstanceDetailsFields: Array<InformationField> = [
    { label: "Name", value: workshopInstanceDetails?.workshopName || "", type: "text" },
    { label: "Datum", value: dayjs(workshopInstanceDetails?.date).format("DD.MM.YYYY") || "", type: "text" },
    { label: "Ort", value: workshopInstanceDetails?.location || "", type: "text" },
  ];

  const handleSubmit = async () => {
    try {
      await submitWorkshopFeedback(userFeedback);
    } catch (error) {
      // Error feedback is handled in the hook, so we don't need to do anything here
    }
  };

  return checkParticipation() ? (
    checkFeedbackGiven() ? (
      <Container sx={{ ml: isMobile ? 0 : 3 }}>
        <Typography variant="h5" component="h1" gutterBottom fontWeight={"bold"}>
          Feedback für {workshopInstanceDetails?.workshopName}
        </Typography>
        <Typography>Vielen Dank, dein Feedback wurde bereits abgegeben!</Typography>
      </Container>
    ) : workshopInstanceDetails &&
      workshopDetails &&
      workshopInstanceFeedbackQuestions &&
      userFeedback.feedback.length > 0 ? (
      <Container sx={{ ml: isMobile ? 0 : 3 }}>
        <Typography variant="h5" component="h1" gutterBottom fontWeight={"bold"}>
          Feedback für {workshopInstanceDetails?.workshopName}
        </Typography>
        <Stack
          component={Paper}
          direction="column"
          sx={{ mt: 2, pt: isMobile ? 1 : 2, pb: isMobile ? 1 : 2, pl: isMobile ? 1 : 3, pr: isMobile ? 1 : 3 }}
          maxWidth={850}
        >
          <Typography variant="h6" color="primary" fontWeight={"bold"}>
            Workshopdetails
          </Typography>
          <InfoSection fields={WorkshopInstanceDetailsFields} />
          <Divider sx={{ borderColor: "#f6891f", mt: 2, mb: 2 }} />
          <Typography variant="h6" fontWeight={"bold"} sx={{ mb: 3 }}>
            Bitte gib Dein Feedback ab:
          </Typography>
          <WorkshopInstanceFeedbackForm
            onSumbit={handleSubmit}
            workshopInstance={workshopInstanceDetails}
            feedbackQuestions={workshopInstanceFeedbackQuestions}
            workshopType={workshopDetails?.type}
            userFeedback={userFeedback}
            updateFeedbackGrade={updateFeedbackGrade}
            updateReferentFeedbackGrade={updateReferentFeedbackGrade}
            setUserFeedback={setUserFeedback}
            workshopInstanceTextQuestions={workshopInstanceTextQuestions}
          />
        </Stack>
      </Container>
    ) : (
      <Container sx={{ ml: isMobile ? 0 : 3 }}>
        <Skeleton variant="text" width="100%" height={60} />
        <Skeleton variant="rounded" width="100%" height={500} />
      </Container>
    )
  ) : (
    <Container sx={{ ml: isMobile ? 0 : 3 }}>
      <Typography variant="h5" component="h1" gutterBottom fontWeight={"bold"}>
        Keine Berechtigung
      </Typography>
    </Container>
  );
};

export default WorkshopInstanceFeedback;
